import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useAppDispatch } from '../../app/hooks.js';
import { extentRequested, flyToRequested } from '../../store/slices/mapSlice.js';
import { searchAndZoom } from '../../store/thunks/searchAndZoom.js';
import { useGetPlacesQuery, type PlaceKind } from '../../services/placesApi.js';
import { parseCoordinates } from './coordinates.js';

/**
 * Move the map to a place.
 *
 * Mirrors the three things the existing viewer's Locate tab does - address,
 * coordinates, and a named area - because those are the three ways people
 * actually navigate Oklahoma data, and long-time users expect all three here.
 */
export default function LocatePanel() {
  const dispatch = useAppDispatch();

  const [address, setAddress] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [coords, setCoords] = useState('');
  const [coordError, setCoordError] = useState<string | null>(null);
  const [areaKind, setAreaKind] = useState<PlaceKind>('county');

  const { data: places = [], isLoading: placesLoading } = useGetPlacesQuery(areaKind);

  const findAddress = async () => {
    setAddressError(null);
    const match = await dispatch(searchAndZoom(address)).unwrap();
    if (!match) setAddressError('No Oklahoma match for that address.');
  };

  const goToCoords = () => {
    const point = parseCoordinates(coords);
    if (!point) {
      setCoordError('Enter a latitude and longitude inside Oklahoma.');
      return;
    }
    setCoordError(null);
    dispatch(flyToRequested({ center: point, zoom: 14 }));
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box component="section" aria-labelledby="locate-address">
        <Typography variant="h3" id="locate-address" sx={{ fontSize: '0.8125rem', mb: 1 }}>
          Street address
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void findAddress(); }}
            placeholder="123 Main St, Norman"
            slotProps={{ htmlInput: { 'aria-label': 'Street address to find' } }}
          />
          <Button variant="outlined" size="small" onClick={() => void findAddress()} disabled={!address.trim()}>
            Find
          </Button>
        </Box>
        {addressError && <Alert severity="info" sx={{ mt: 1, py: 0 }}>{addressError}</Alert>}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Addresses resolve through the US Census Geocoder.
        </Typography>
      </Box>

      <Divider />

      <Box component="section" aria-labelledby="locate-coords">
        <Typography variant="h3" id="locate-coords" sx={{ fontSize: '0.8125rem', mb: 1 }}>
          Coordinates
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={coords}
            onChange={(e) => setCoords(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') goToCoords(); }}
            placeholder="35.4676, -97.5164"
            error={Boolean(coordError)}
            slotProps={{ htmlInput: { 'aria-label': 'Latitude and longitude' } }}
          />
          <Button variant="outlined" size="small" onClick={goToCoords} disabled={!coords.trim()}>
            Go
          </Button>
        </Box>
        {coordError && <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{coordError}</Alert>}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Decimal degrees or degrees/minutes/seconds. State Plane and UTM entry are not in this
          prototype.
        </Typography>
      </Box>

      <Divider />

      <Box component="section" aria-labelledby="locate-area">
        <Typography variant="h3" id="locate-area" sx={{ fontSize: '0.8125rem', mb: 1 }}>
          Zoom to an area
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={areaKind}
          onChange={(_, v: PlaceKind | null) => v && setAreaKind(v)}
          aria-label="Area type"
          sx={{ mb: 1 }}
        >
          <ToggleButton value="county" sx={{ px: 2, py: 0.25, fontSize: '0.75rem' }}>County</ToggleButton>
          <ToggleButton value="city" sx={{ px: 2, py: 0.25, fontSize: '0.75rem' }}>City or town</ToggleButton>
        </ToggleButtonGroup>

        <Autocomplete
          size="small"
          options={[...places]}
          loading={placesLoading}
          getOptionLabel={(p) => p.name}
          isOptionEqualToValue={(a, b) => a.name === b.name}
          onChange={(_, place) => { if (place) dispatch(extentRequested({ bbox: place.bbox })); }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={areaKind === 'county' ? 'Choose a county' : 'Choose a city or town'}
              // MUI v9 moved the raw input props into slotProps.htmlInput, so
              // the accessible name is merged there rather than via inputProps.
              slotProps={{
                ...params.slotProps,
                htmlInput: {
                  ...(params.slotProps?.htmlInput ?? {}),
                  'aria-label': areaKind === 'county' ? 'Choose a county' : 'Choose a city or town',
                },
              }}
            />
          )}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {placesLoading ? 'Loading…' : `${places.length} ${areaKind === 'county' ? 'counties' : 'places'}`}
        </Typography>
      </Box>
    </Box>
  );
}
